import React, { useState } from "react";

type DiscountReasonType = "employee_discount" | "damaged" | "expired" | "price_match";

const REASON_OPTIONS: { value: DiscountReasonType; label: string }[] = [
  { value: "employee_discount", label: "Employee Discount" },
  { value: "damaged", label: "Damaged Product" },
  { value: "expired", label: "Expired Product" },
  { value: "price_match", label: "Price Match" },
];

interface DiscountModalProps {
  title: string;
  subtitle?: string;
  onConfirm: (type: "percent_off" | "dollar_off", value: number, reason: string, reasonType?: DiscountReasonType) => void;
  onClose: () => void;
  onRemove?: () => void;
  hasExistingDiscount?: boolean;
}

export default function DiscountModal({
  title,
  subtitle,
  onConfirm,
  onClose,
  onRemove,
  hasExistingDiscount,
}: DiscountModalProps) {
  const [discountType, setDiscountType] = useState<"percent_off" | "dollar_off">("percent_off");
  const [value, setValue] = useState("");
  const [reasonType, setReasonType] = useState<DiscountReasonType | "">("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = () => {
    const parsed = parseFloat(value);
    if (!value || isNaN(parsed) || parsed <= 0) {
      setError("Enter a valid discount value");
      return;
    }
    if (discountType === "percent_off" && parsed > 100) {
      setError("Percent cannot exceed 100%");
      return;
    }
    if (!reasonType) {
      setError("Select a discount reason");
      return;
    }
    if (!notes.trim()) {
      setError("Notes are required");
      return;
    }
    setError("");
    // Combine reason type label + notes for the stored reason string
    const reasonLabel = REASON_OPTIONS.find(r => r.value === reasonType)?.label || reasonType;
    const fullReason = `${reasonLabel}: ${notes.trim()}`;
    onConfirm(discountType, parsed, fullReason, reasonType as DiscountReasonType);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="rounded-2xl shadow-2xl w-full max-w-sm p-6"
        style={{ background: "var(--rbw-bg-card)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold mb-1" style={{ color: "var(--rbw-navy)" }}>
          {title}
        </h3>
        {subtitle && (
          <p className="text-sm mb-4" style={{ color: "var(--rbw-gray-dark)" }}>
            {subtitle}
          </p>
        )}

        {/* Discount type toggle */}
        <div className="flex rounded-xl overflow-hidden mb-4" style={{ border: "2px solid var(--rbw-border)" }}>
          <button
            onClick={() => { setDiscountType("percent_off"); setValue(""); }}
            className="flex-1 py-2.5 font-bold text-sm transition-all"
            style={{
              background: discountType === "percent_off" ? "var(--rbw-navy)" : "transparent",
              color: discountType === "percent_off" ? "var(--rbw-gold)" : "var(--rbw-gray-dark)",
            }}
          >
            % Off
          </button>
          <button
            onClick={() => { setDiscountType("dollar_off"); setValue(""); }}
            className="flex-1 py-2.5 font-bold text-sm transition-all"
            style={{
              background: discountType === "dollar_off" ? "var(--rbw-navy)" : "transparent",
              color: discountType === "dollar_off" ? "var(--rbw-gold)" : "var(--rbw-gray-dark)",
              borderLeft: "2px solid var(--rbw-border)",
            }}
          >
            $ Off
          </button>
        </div>

        <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--rbw-gray-dark)" }}>
          {discountType === "percent_off" ? "Discount Percent" : "Discount Amount ($)"}
        </label>
        <div className="relative mb-3">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg font-bold" style={{ color: "var(--rbw-gray-dark)" }}>
            {discountType === "percent_off" ? "%" : "$"}
          </span>
          <input
            type="number"
            min="0.01"
            max={discountType === "percent_off" ? "100" : undefined}
            step={discountType === "percent_off" ? "1" : "0.01"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={discountType === "percent_off" ? "10" : "0.00"}
            className="w-full pl-9 pr-3 py-2.5 rounded-lg outline-none text-lg font-bold"
            style={{
              border: "2px solid var(--rbw-border)",
              color: "var(--rbw-navy)",
              background: "var(--rbw-bg)",
            }}
            onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
            onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
            autoFocus
          />
        </div>

        {/* Structured reason dropdown */}
        <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--rbw-gray-dark)" }}>
          Reason (required)
        </label>
        <select
          value={reasonType}
          onChange={(e) => setReasonType(e.target.value as DiscountReasonType | "")}
          className="w-full px-3 py-2.5 rounded-lg mb-3 outline-none text-sm"
          style={{
            border: "2px solid var(--rbw-border)",
            color: reasonType ? "var(--rbw-navy)" : "var(--rbw-gray-dark)",
            background: "var(--rbw-bg)",
          }}
          onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
          onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
        >
          <option value="">Select a reason...</option>
          {REASON_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        {/* Notes field */}
        <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--rbw-gray-dark)" }}>
          Notes (required)
        </label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Damaged label on front, Customer showed competitor ad"
          className="w-full px-3 py-2.5 rounded-lg mb-3 outline-none text-sm"
          style={{
            border: "2px solid var(--rbw-border)",
            color: "var(--rbw-navy)",
            background: "var(--rbw-bg)",
          }}
          onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
          onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
          onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
        />

        {error && (
          <p className="text-xs font-medium mb-3" style={{ color: "var(--rbw-danger)" }}>
            {error}
          </p>
        )}

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl font-bold text-sm transition-all active:scale-95"
            style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-gray-dark)" }}
          >
            Cancel
          </button>
          {hasExistingDiscount && onRemove && (
            <button
              onClick={onRemove}
              className="py-2.5 px-4 rounded-xl font-bold text-sm transition-all active:scale-95"
              style={{ background: "#FEE2E2", color: "var(--rbw-danger)" }}
            >
              Remove
            </button>
          )}
          <button
            onClick={handleSubmit}
            className="flex-1 py-2.5 rounded-xl font-bold text-sm transition-all active:scale-95"
            style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
          >
            Apply Discount
          </button>
        </div>
      </div>
    </div>
  );
}
